import type { ConnectorProvider, ConnectorSurface } from '@ralph/contracts'
import { SURFACE_LABEL } from '@ralph/contracts'
import clsx from 'clsx'

/**
 * A provider's mark, drawn as path data in the product's own ink.
 *
 * Not the vendors' logos. A logotype is a trademark with usage rules and a palette of its
 * own, and four of them in four palettes beside each other is a row of adverts. These are
 * schematic glyphs — a library, a cloud, a triangle of drives, a bucket — in `currentColor`,
 * so they follow the theme and the brand like every other icon on the page. Each carries its
 * name for assistive technology; the word appears beside it wherever the surface matters.
 */
export const ProviderMark = ({
  surface,
  provider,
  size = 14,
  className
}: {
  surface?: ConnectorSurface | null
  provider?: ConnectorProvider
  size?: number
  className?: string
}) => {
  const which: ConnectorSurface | null =
    surface ??
    (provider === 'microsoft' ? 'sharepoint' : provider === 'google' ? 'drive' : provider === 's3' ? 's3' : null)
  const label = which ? SURFACE_LABEL[which] : 'Storage provider'
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={label}
      className={clsx('shrink-0', className)}
    >
      {which === 'sharepoint' && (
        <>
          <circle cx="6.5" cy="6" r="3.75" />
          <circle cx="10" cy="9.5" r="3.25" />
          <circle cx="6" cy="11.5" r="2.25" />
        </>
      )}
      {which === 'onedrive' && (
        <path d="M4.5 12.5h7.25a2.75 2.75 0 0 0 .4-5.47A4 4 0 0 0 4.6 6.2 3.2 3.2 0 0 0 4.5 12.5Z" />
      )}
      {which === 'drive' && (
        <>
          <path d="M6 2.5h4l4 7h-4Z" />
          <path d="M2 9.5l4-7 2 3.5-4 7Z" />
          <path d="M2 9.5h8l2 3.5H4Z" />
        </>
      )}
      {which === 's3' && (
        <>
          <ellipse cx="8" cy="4" rx="5" ry="1.75" />
          <path d="M3 4v1.5c0 .97 2.24 1.75 5 1.75s5-.78 5-1.75V4" />
          <path d="M3.4 6.2 4.5 12.5c.2 1 1.8 1.5 3.5 1.5s3.3-.5 3.5-1.5l1.1-6.3" />
        </>
      )}
      {which === null && <rect x="2.5" y="3.5" width="11" height="9" rx="1.5" />}
    </svg>
  )
}
